import type { Track } from '../interfaces/track';

const coerceRecordingId = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : undefined;
  }
  return undefined;
};

const inspectCandidate = (
  candidate: unknown,
  visited: Set<unknown> = new Set(),
  assumeRecording = false
): string | undefined => {
  if (candidate === null || candidate === undefined) {
    return undefined;
  }

  if (typeof candidate === 'string' || typeof candidate === 'number') {
    return coerceRecordingId(candidate);
  }

  if (typeof candidate === 'object') {
    if (visited.has(candidate)) {
      return undefined;
    }
    visited.add(candidate);

    if (Array.isArray(candidate)) {
      for (const entry of candidate) {
        const resolved = inspectCandidate(entry, visited);
        if (resolved) return resolved;
      }
      return undefined;
    }

    const record = candidate as Record<string, any>;
    const directKeys = [
      'recordingId',
      'recording_id',
      'recordingID',
      'catalogRecordingId',
      'catalog_recording_id',
      'catalogRecordingID',
      'catalog_recordingID',
      'catalogRecording_id',
      'catalog_recordingId',
      'recordingUuid',
      'recording_uuid',
      'catalogRecordingUuid',
      'catalog_recording_uuid',
      'recordingSlug',
      'recording_slug',
      'catalogRecordingSlug',
      'catalog_recording_slug',
      'uuid',
      'slug',
    ];

    if (assumeRecording) {
      const assumedId = coerceRecordingId(record.id);
      if (assumedId) return assumedId;
    }

    for (const key of directKeys) {
      const resolved = coerceRecordingId(record[key]);
      if (resolved) return resolved;
    }

    for (const [key, value] of Object.entries(record)) {
      const normalizedKey = key.toLowerCase();

      if (normalizedKey === 'id') {
        if (assumeRecording) {
          const resolved = coerceRecordingId(value);
          if (resolved) return resolved;
        }

        const type = typeof record.type === 'string' ? record.type.toLowerCase() : null;
        if (type === 'recording') {
          const resolved = coerceRecordingId(value);
          if (resolved) return resolved;
        }
        if (record.recording || record.recordingId || record.recording_id || record.catalogRecordingId) {
          const resolved = coerceRecordingId(value);
          if (resolved) return resolved;
        }
      }

      if (normalizedKey.includes('recording') && normalizedKey.endsWith('id')) {
        const resolved = coerceRecordingId(value);
        if (resolved) return resolved;
      }

      if (normalizedKey.includes('recording') && normalizedKey.endsWith('uuid')) {
        const resolved = coerceRecordingId(value);
        if (resolved) return resolved;
      }

      if (normalizedKey.includes('recording') && normalizedKey.endsWith('slug')) {
        const resolved = coerceRecordingId(value);
        if (resolved) return resolved;
      }

      if (normalizedKey.includes('recording') && normalizedKey.endsWith('ids')) {
        const resolved = inspectCandidate(value, visited, true);
        if (resolved) return resolved;
      }
    }

    const metaCandidates = [record._meta, record.meta, record.metadata];
    for (const meta of metaCandidates) {
      const resolved = inspectCandidate(meta, visited, assumeRecording);
      if (resolved) return resolved;
    }

    const nestedKeys = [
      'recording',
      'catalogRecording',
      'catalog_recording',
      'recordingDetails',
      'recording_details',
      'recordingInfo',
      'recording_info',
      'recordingMetadata',
      'recording_metadata',
      'catalogRecordingDetails',
      'catalog_recording_details',
      'recordingData',
      'recording_data',
      'catalogRecordingData',
      'catalog_recording_data',
    ];

    for (const key of nestedKeys) {
      const resolved = inspectCandidate(record[key], visited, true);
      if (resolved) return resolved;
    }

    const collectionCandidates = [
      record.recordings,
      record.catalogRecordings,
      record.catalog_recordings,
      record.relatedRecordings,
      record.related_recordings,
    ];

    for (const collection of collectionCandidates) {
      const resolved = inspectCandidate(collection, visited, true);
      if (resolved) return resolved;
    }
  }

  return undefined;
};

export const resolveTrackRecordingId = (track: Track | Spotify.Track): string | undefined => {
  const buckets: Array<Record<string, any> | undefined> = [
    track as Record<string, any>,
    (track as any)?.metadata,
    (track as any)?.classical,
    (track as any)?.catalog,
    (track as any)?.catalogMetadata,
  ];

  for (const bucket of buckets) {
    if (!bucket || typeof bucket !== 'object') continue;
    const resolved = inspectCandidate(bucket, new Set());
    if (resolved) return resolved;
  }

  return undefined;
};
